import type {
	SRT_RowSelectionState,
	SRT_SortingState,
} from '@/shared/components/master-table'
import type { SettingsTemplateModel } from '@/shared/generated'
import type { TFunction } from 'i18next'

import React, { useCallback, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { all, any, prop } from 'lodash/fp'
import { CheckIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
	getOptimizationsMap,
	getOptStatus,
	getStatusBadgeVariant,
	RevokeOptimizationAction,
	useOptimizationsQuery,
} from '@/entities/optimization'
import { useOptimizationGroupsQuery } from '@/entities/optimization-group'
import { useApplySettingsTemplateMutation } from '@/entities/settings-template'

import {
	createSRTColumnHelper,
	MasterTable,
	useMasterReactTable,
} from '@/shared/components/master-table'
import { SRT_TableToolbar } from '@/shared/components/master-table/components/tools/SRT_TableToolbar'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Blanket } from '@/shared/components/ui/blanket'
import { Button } from '@/shared/components/ui/button'
import { confirm } from '@/shared/components/ui/confirmer'
import { Loader } from '@/shared/components/ui/loader'
import {
	ModalBody,
	ModalDescription,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { Tag } from '@/shared/components/ui/tag'
import { ApplyAction, OptimizationStatus } from '@/shared/generated'
import { getMasterTableLocale } from '@/shared/i18n'

export function ApplyToOptimizationGroups({
	template,
	close,
	action,
}: {
	template: SettingsTemplateModel
	close: () => void
	action: ApplyAction
}) {
	const { t } = useTranslation()

	const queryClient = useQueryClient()
	const [rowSelection, setRowSelection] = React.useState<SRT_RowSelectionState>(
		{}
	)
	const optimizationGroupsQuery = useOptimizationGroupsQuery()
	const optimizationsQuery = useOptimizationsQuery({
		select: getOptimizationsMap,
	})
	const applyMutation = useApplySettingsTemplateMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['optimizations'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns-overview'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaign'],
			})
			queryClient.invalidateQueries({
				queryKey: ['scenarios'],
			})
			queryClient.invalidateQueries({
				queryKey: ['scenarios-query-list'],
			})
		},
	})
	const isLoading = any(prop('isLoading'), [
		optimizationGroupsQuery,
		optimizationsQuery,
	])
	const data = useMemo(
		() =>
			optimizationGroupsQuery.data?.map((og) => ({
				id: og.id,
				name: og.name,
				status: getOptStatus(optimizationsQuery.data?.[og.id]?.status),
			})) || [],
		[optimizationsQuery, optimizationGroupsQuery]
	)
	const allSelectedItemsAreValid = useMemo(
		() =>
			action === ApplyAction.AddWhatIfScenario ||
			all(
				({ status }) =>
					[OptimizationStatus.New, OptimizationStatus.Pending].includes(
						status!
					),
				data.filter((row) => rowSelection[row.id])
			),
		[rowSelection, data, action]
	)
	const canGoNext = useMemo(
		() => Object.keys(rowSelection).length > 0 && allSelectedItemsAreValid,
		[allSelectedItemsAreValid, rowSelection]
	)
	const handleApply = useCallback(async () => {
		const answer = await confirm({
			title: t('template.apply.confirm.title', {
				defaultValue: 'Apply settings template',
			}),
			description: t('template.apply.confirm.text', {
				defaultValue: 'Are you sure you want to apply the template',
			}),
			actionText: t('app.apply', { defaultValue: 'Apply' }),
			cancelText: t('general_cancel'),
		})
		if (answer) {
			await applyMutation.mutateAsync({
				id: template.id,
				data: { ids: Object.keys(rowSelection), action },
			})
			toast.success(t('toast.entity.applied', { entity: t('entity.template') }))
			close()
		}
	}, [rowSelection, action])
	const columnVisibility = useMemo(
		() =>
			/**
			 * Hide status and id columns for Add What-if Scenario action
			 */
			action === ApplyAction.AddWhatIfScenario
				? {
						status: false,
						id: false,
					}
				: undefined,
		[action]
	)
	const table = useOptimizationGroupsTable({
		isLoading,
		data,
		rowSelection,
		setRowSelection,
		columnVisibility,
	})

	return (
		<>
			<Blanket
				isTinted={isLoading || applyMutation.isPending}
				className='absolute flex items-center justify-center rounded-lg bg-white/30 backdrop-blur-xs dark:bg-black/30'
			>
				<Loader />
			</Blanket>
			<ModalHeader>
				<ModalTitle>
					{t('template.apply.modal.title', {
						name: template.name,
						defaultValue: `Apply settings template: ${template.name}`,
					})}
				</ModalTitle>
				<ModalDescription>
					{t('template.apply.modal.subtitle', {
						defaultValue:
							'This will apply the settings from the template to the current settings. Are you sure you want to apply the template',
					})}
				</ModalDescription>
			</ModalHeader>
			<ModalBody>
				{applyMutation.isError && (
					<Alert variant='destructive' className='mb-2'>
						<AlertDescription>
							{applyMutation.error?.message ||
								t('template.apply.error', {
									defaultValue:
										'We could not apply the template. Please ensure that the selected items are in a valid state, or try again later.',
								})}
						</AlertDescription>
					</Alert>
				)}
				<div className='pb-2'>
					<SRT_TableToolbar table={table} />
				</div>
				<div className='border-base flex h-96 flex-1 flex-col overflow-hidden rounded-lg border border-solid'>
					<MasterTable table={table} />
				</div>
			</ModalBody>
			<ModalFooter className='flex justify-between'>
				<Button data-testid='apply-template-modal-cancel' onClick={close}>
					{t('general_cancel')}
				</Button>
				<Button
					data-testid='apply-template-modal-apply'
					variant='primary'
					disabled={!canGoNext || applyMutation.isPending}
					onClick={handleApply}
				>
					<CheckIcon />
					{t('general_apply')}
				</Button>
			</ModalFooter>
		</>
	)
}
type OptimizationGroupDataRow = {
	id: string
	name: string
	status?: OptimizationStatus
}
function useOptimizationGroupsTable({
	isLoading,
	data,
	rowSelection,
	setRowSelection,
	columnVisibility,
}: {
	isLoading?: boolean
	data: OptimizationGroupDataRow[]
	rowSelection: SRT_RowSelectionState
	columnVisibility?: Record<string, boolean>
	setRowSelection: React.Dispatch<React.SetStateAction<SRT_RowSelectionState>>
}) {
	const { t } = useTranslation()

	const [sorting, setSorting] = useState<SRT_SortingState>([
		{
			desc: false,
			id: 'name',
		},
	])

	return useMasterReactTable<OptimizationGroupDataRow>({
		localization: getMasterTableLocale(),
		data,
		columns: getColumns(t),
		state: {
			isLoading,
			sorting,
			rowSelection,
			columnVisibility,
		},
		layoutMode: 'grid',
		divideX: false,
		getRowId: (row) => row.id,
		enableColumnActions: false,
		enableColumnFilters: true,
		enableRowSelection: true,
		enableSorting: true,
		enableColumnPinning: false,
		onRowSelectionChange: setRowSelection,
		defaultColumn: {
			minSize: 28,
			size: Number.MAX_SAFE_INTEGER,
			maxSize: Number.MAX_SAFE_INTEGER,
		},
		onSortingChange: setSorting,
	})
}
const columnHelper = createSRTColumnHelper<OptimizationGroupDataRow>()
const getColumns = (t: TFunction) => [
	columnHelper.accessor('name', {
		header: t('optimization_group'),
		meta: {
			label: t('optimization_group'),
			placeholder: t('optimization_group.placeholder', {
				defaultValue: 'Search optimization group ...',
			}),
			variant: 'text',
		},

		Cell: ({ cellValue }) => (
			<div className='truncate text-sm'>{cellValue}</div>
		),
	}),
	columnHelper.accessor('id', {
		size: 100,
		enableSorting: false,
		enablePinning: false,
		enableHiding: false,
		header: t('actions', { defaultValue: 'Actions' }),
		Cell: ({ row }) => (
			<div className='flex items-center justify-end space-x-2'>
				<RevokeOptimizationAction
					optimizationGroupId={row.original.id}
					status={row.original.status}
				/>
			</div>
		),
	}),
	columnHelper.accessor('status', {
		header: t('app.status'),
		size: 130,
		Cell: ({ cellValue }) => {
			const { t } = useTranslation()

			const value = cellValue

			return (
				<Tag variant={getStatusBadgeVariant(value)}>{t(`opt_${value}`)}</Tag>
			)
		},
	}),
]
